import { MutableRefObject, useEffect, useRef, useState } from 'react';
import xlsx, { type IContent } from 'json-as-xlsx';
import { download } from 'utils/download';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GridApi, ColumnApi, IDatasource } from 'ag-grid-community';
import Session from 'modules/Session';
import { Store } from 'components/common/types/Store.types';
import { ColumnDef } from 'components/common/types/Table.types';
import PageSize from 'lib/PageSize';
import Table from 'lib/Table/Table';
import { TableHeight, TableRowHeight } from 'lib/Table/Table.types';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getDirectSalesFilteredCampaignsTable } from 'modules/api/directSalesCampaigns';
import { PAGE_SIZE_LARGE } from 'consts/pagination';
import { getValidPageSize } from 'utils/pageSize';
import { formatDDMMYYYYDate } from 'utils/dateFormatUtil';
import { isEmpty } from 'lodash';
import { usePageFilters } from 'customHooks/usePageFilters';
import IconButton from 'lib/IconButton';
import DropdownMenu from 'lib/DropdownMenu';
import { Color } from 'lib/Button';
import { useOutsideClick } from 'customHooks/useOutsideClick';
import { formatExportDateAndTime } from 'utils/formatExportDateAndTime';
import { DirectSalesFilteredCampaignsTableResponse } from 'components/common/types/DirectSalesCampaign.types';
import tableSvg from 'assets/icons/table.svg';
import downloadSvg from 'assets/icons/download.svg';
import resetSvg from 'assets/icons/reset.svg';
import DealIdRenderer from 'components/common/DealIdRenderer';
import CopyToClipboardCellRenderer from 'components/common/CopyToClipboardCellRenderer';
import ActionRenderer from './ActionRenderer/ActionRenderer';
import { DIRECT_SALES_CAMPAIGN_PAGE_FILTERS_KEY } from '../Filters/consts';
import { DirectSalesDealManagementFilters } from '../Filters/Filters.types';
import { StatusRenderer } from '../../common/StatusRenderer/StatusRenderer';
import { sortColumnsBy } from './utils';
import OOCReport from '../OOCReport';
import OOCRenderer from './OOCRenderer';
import './DealsTable.pcss';
import { DESC_FIRST_SORTING_ORDER, TABLE_EXPORT_COLUMNS, formatMediaTypeLabels } from './consts';

const pageKey = 'directSalesCampaignsTable';

const DealsTable = (): React.ReactElement => {
  const navigate = useNavigate();
  const { marketId, localeCode } = useSelector((state: Store) => state.publisher.configuration);

  const [tableContent, setTableContent] = useState<DirectSalesFilteredCampaignsTableResponse[]>([]);
  const [pageSize, setPageSize] = useState(getValidPageSize(Session.getPageSizeByKey(pageKey) || PAGE_SIZE_LARGE));
  const tableApiRef: MutableRefObject<GridApi | undefined> = useRef();
  const columnApiRef: MutableRefObject<ColumnApi | undefined> = useRef();
  const tableRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const { filters } = usePageFilters<DirectSalesDealManagementFilters>(DIRECT_SALES_CAMPAIGN_PAGE_FILTERS_KEY);

  const getDataSource = (): IDatasource => ({
    getRows: async (params) => {
      const [sortingColumn] = params.sortModel;
      const sortBy = sortingColumn ? sortColumnsBy(sortingColumn.colId) : undefined;
      tableApiRef.current?.showLoadingOverlay();

      try {
        const { content, totalElements } = await getDirectSalesFilteredCampaignsTable({
          externalPublisherId: marketId,
          cancelFunctions,
          filters,
          size: pageSize,
          page: params.startRow / pageSize,
          sort: {
            sortBy,
            sortDir: sortBy ? sortingColumn.sort.toUpperCase() : undefined,
          },
        });

        if (totalElements === 0) {
          tableApiRef.current?.showNoRowsOverlay();
        } else {
          tableApiRef.current?.hideOverlay();
        }

        setTableContent(content);
        params.successCallback(content, totalElements);
      } catch {
        params.failCallback();
      }
    },
  });

  const onPageSizeChange = (size: number): void => {
    setPageSize(size);
    Session.setPageSizeByKey(pageKey, size);
    tableApiRef.current?.paginationSetPageSize(size);
    tableApiRef.current?.setDatasource(getDataSource());
  };

  const getTableApi = (tableApi: GridApi): void => {
    tableApiRef.current = tableApi;
    tableApi.setDatasource(getDataSource());
  };

  const getColumnApi = (columnApi: ColumnApi): void => {
    columnApiRef.current = columnApi;
  };

  const columnDefs: ColumnDef[] = [
    {
      headerName: 'Out of charge frames',
      field: '',
      colId: 'oocFrames',
      minWidth: 40,
      headerTooltip: '',
      cellRenderer: ({ data }) => OOCRenderer({ campaignId: data?.id }),
      resizable: false,
      sortable: false,
      headerClass: 'opacity-0',
      suppressColumnsToolPanel: true,
    },
    {
      headerName: 'Actions (Edit / Duplicate)',
      field: 'actions',
      minWidth: 90,
      headerTooltip: '',
      cellRenderer: ActionRenderer,
      resizable: false,
      sortable: false,
      headerClass: 'opacity-0',
    },
    {
      headerName: 'Status',
      field: 'campaignStatus',
      minWidth: 90,
      headerTooltip: 'Campaign status',
      cellRenderer: ({ data }) => StatusRenderer({ campaignStatus: data?.campaignStatus }),
      colId: 'status',
    },
    {
      headerName: 'Campaign name',
      field: 'campaignName',
      minWidth: 250,
      headerTooltip: '',
      cellRenderer: ({ data }) =>
        data?.campaignName ? (
          <CopyToClipboardCellRenderer value={data.campaignName} colorClasses="hover:bg-purple-100" />
        ) : null,
    },
    {
      headerName: 'ID',
      field: 'userVisibleId',
      minWidth: 160,
      headerTooltip: '',
      valueGetter: ({ data }) => data?.userVisibleId || data?.reference,
      cellRenderer: ({ data }) =>
        DealIdRenderer({
          data: { dealId: data?.userVisibleId || data?.reference, isChildRow: false },
          isDSMEnvironment: true,
        }),
    },
    {
      headerName: 'Media type',
      field: 'campaignMediaTypes',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        if (!data?.campaignMediaTypes?.length) return null;
        return <span>{formatMediaTypeLabels(data.campaignMediaTypes)}</span>;
      },
      sortable: false,
    },
    {
      headerName: 'From',
      field: 'startDate',
      minWidth: 120,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{formatDDMMYYYYDate(data?.startDate, localeCode)}</span>;
      },
    },
    {
      headerName: 'To',
      field: 'endDate',
      minWidth: 120,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{formatDDMMYYYYDate(data?.endDate, localeCode)}</span>;
      },
    },
    {
      headerName: 'Created date',
      field: 'createdAt',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{formatDDMMYYYYDate(data?.createdAt, localeCode)}</span>;
      },
    },
    {
      headerName: 'Updated date',
      field: 'updatedAt',
      initialSort: 'desc',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{formatDDMMYYYYDate(data?.updatedAt, localeCode)}</span>;
      },
    },
    {
      headerName: 'Sales team',
      field: 'salesTeam',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{data?.salesTeam}</span>;
      },
    },
    {
      headerName: 'Sales person',
      field: 'salesPerson',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{data?.salesPerson}</span>;
      },
    },
    {
      headerName: 'Admin person',
      field: 'adminPerson',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{data?.adminPerson}</span>;
      },
    },
    {
      headerName: 'Advertiser',
      field: 'advertiser',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{data?.advertiser}</span>;
      },
    },
    {
      headerName: 'Brand',
      field: 'brand',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{data?.brand}</span>;
      },
    },
    {
      headerName: 'Product category',
      field: 'productCategory',
      minWidth: 150,
      headerTooltip: 'Product category',
      cellRenderer: ({ data }) => {
        return <span>{data?.productCategory}</span>;
      },
    },
    {
      headerName: 'Agency',
      field: 'agency',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{data?.agency}</span>;
      },
    },
    {
      headerName: 'Specialist',
      field: 'specialist',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{data && data.specialist}</span>;
      },
    },
    {
      headerName: 'Updated by',
      field: 'updatedBy',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{data?.updatedBy}</span>;
      },
    },
    {
      headerName: 'Drop date',
      field: 'dropDate',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{formatDDMMYYYYDate(data?.dropDate, localeCode)}</span>;
      },
    },
    {
      headerName: 'Created by',
      field: 'createdBy',
      minWidth: 150,
      headerTooltip: '',
      cellRenderer: ({ data }) => {
        return <span>{data?.createdBy}</span>;
      },
    },
  ];

  useEffect(() => {
    if (!isEmpty(filters) && tableApiRef.current) {
      tableApiRef.current?.setDatasource(getDataSource());
    }
  }, [filters]);

  const showSideBar = (): void => {
    if (!tableApiRef.current) return;

    tableApiRef.current.setSideBarVisible(true);
    tableApiRef.current.openToolPanel('columns');
  };

  const hideSideBar = (): void => {
    if (!tableApiRef.current) return;

    tableApiRef.current.setSideBarVisible(false);
    tableApiRef.current.closeToolPanel();
  };

  const exportTableAsCsv = (): void => {
    if (!tableContent.length) return;

    const headers = TABLE_EXPORT_COLUMNS.map(({ label }) => label).join(',');
    const rows = tableContent.map((row) =>
      TABLE_EXPORT_COLUMNS.map(({ value }) => {
        const cell =
          typeof value === 'function'
            ? value(row as unknown as IContent)
            : row[value as keyof DirectSalesFilteredCampaignsTableResponse];
        return cell != null ? `"${cell.toString().replace(/"/g, '""')}"` : '';
      }).join(','),
    );

    const csvBlob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    download(csvBlob, `direct_sales_deals_${formatExportDateAndTime(new Date())}.csv`);
  };

  const exportTableAsXlsx = (): void => {
    if (!tableContent.length) return;

    xlsx(
      [
        {
          sheet: 'Sheet 1',
          columns: TABLE_EXPORT_COLUMNS,
          content: tableContent as unknown as IContent[],
        },
      ],
      { fileName: `direct_sales_deals_${formatExportDateAndTime(new Date())}` },
    );
  };

  const resetTableColumnOrderAndVisibility = (): void => {
    if (!tableApiRef.current || !columnApiRef.current) return;

    const originalColumnIds = columnDefs.map((colDef) => colDef.colId || colDef.field);

    const resetColumnState = originalColumnIds.map((colId) => ({
      colId,
      hide: false,
    }));

    columnApiRef.current.applyColumnState({
      state: resetColumnState,
      applyOrder: true,
    });

    tableApiRef.current.refreshHeader();
  };

  const handleOutsideClick = (): void => {
    if (tableApiRef.current?.isSideBarVisible()) {
      hideSideBar();
    }
  };

  useOutsideClick(tableRef, handleOutsideClick);

  return (
    <div ref={tableRef} className="h-full flex flex-col">
      <div className="flex justify-between items-center">
        <div className="mb-3">
          <PageSize
            dataTestId="page-size-selection"
            onPageSizeChange={onPageSizeChange}
            pageSize={pageSize}
            pageInfoText="campaigns per page"
          />
        </div>
        <div className="flex gap-2">
          <IconButton icon={resetSvg} name="reset" handleOnClick={resetTableColumnOrderAndVisibility} />
          <IconButton
            icon={tableSvg}
            name="menu"
            handleOnClick={() => {
              if (!tableApiRef?.current?.isSideBarVisible()) {
                showSideBar();
              } else {
                hideSideBar();
              }
            }}
          />
          <OOCReport />
          <div className="w-px h-6 bg-neutral-950-opacity-10 self-center mx-1" />
          <DropdownMenu
            dropdownMenuOptions={[
              {
                id: 'table-as-csv',
                onClick: exportTableAsCsv,
                label: 'Export as CSV',
              },
              {
                id: 'table-as-xlsx',
                onClick: exportTableAsXlsx,
                label: 'Export as XLSX',
              },
            ]}
            icon={downloadSvg}
            color={Color.TRANSPARENT}
            label="Export table"
            onOpen={hideSideBar}
            closeOnSelection
          />
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <Table
          dataTestId="direct-sales-campaigns-table"
          key={pageSize}
          columnDefs={columnDefs}
          getTableApi={getTableApi}
          getColumnApi={getColumnApi}
          extraGridOptions={{
            rowModelType: 'infinite',
            rowHeight: TableRowHeight.NORMAL,
            pagination: true,
            paginationPageSize: pageSize,
            cacheBlockSize: pageSize,
            enableCellTextSelection: true,
            defaultColDef: {
              resizable: true,
              wrapHeaderText: true,
              suppressMenu: true,
              sortable: true,
              sortingOrder: DESC_FIRST_SORTING_ORDER,
            },
            sideBar: {
              toolPanels: [
                {
                  id: 'columns',
                  labelDefault: 'Columns',
                  labelKey: 'columns',
                  iconKey: 'columns',
                  toolPanel: 'agColumnsToolPanel',
                },
              ],
              defaultToolPanel: 'columns',
              hiddenByDefault: true,
            },
          }}
          tableHeight={TableHeight.AUTO}
          onRowClicked={(row) => {
            if (!row.event?.defaultPrevented) navigate(`/planner/direct-sales/${row.data.reference}`);
          }}
          suppressMovableColumns={false}
          className="direct-sales-campaigns-table ag-theme-alpine"
        />
      </div>
    </div>
  );
};

export default DealsTable;
